/**
 * Chat conversation helpers (M0.2 wrap-up): usage/cost roll-up shown in the chat
 * header and safe deletion of a conversation.
 */
import type { Usage } from '@luminis/protocol';
import type { ChatConversation } from './types';
import { estimateCost } from './settings';

export interface ConversationUsage {
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  /** Assistant turns that reported usage. */
  turns_with_usage: number;
  /** Estimate in CNY; uses reported `cost_cny` when available. */
  cost_cny: number;
}

export function conversationUsage(
  conversation: ChatConversation | null | undefined,
): ConversationUsage {
  const usage: ConversationUsage = {
    input_tokens: 0,
    output_tokens: 0,
    total_tokens: 0,
    turns_with_usage: 0,
    cost_cny: 0,
  };
  if (!conversation) return usage;
  for (const turn of conversation.turns) {
    const turnUsage: Usage | undefined = turn.usage;
    if (!turnUsage) continue;
    usage.input_tokens += turnUsage.input_tokens ?? 0;
    usage.output_tokens += turnUsage.output_tokens ?? 0;
    usage.turns_with_usage += 1;
    usage.cost_cny +=
      typeof turnUsage.cost_cny === 'number' && Number.isFinite(turnUsage.cost_cny)
        ? turnUsage.cost_cny
        : estimateCost({
            input_tokens: turnUsage.input_tokens ?? 0,
            output_tokens: turnUsage.output_tokens ?? 0,
          });
  }
  usage.total_tokens = usage.input_tokens + usage.output_tokens;
  return usage;
}

/** Remove one conversation; returns a new array and never mutates the input. */
export function removeConversation(
  conversations: readonly ChatConversation[],
  id: string,
): ChatConversation[] {
  return conversations.filter((conversation) => conversation.id !== id);
}

/** Title for the conversation select: first user message fallback to stored title. */
export function conversationLabel(conversation: ChatConversation): string {
  const stored = conversation.title.trim();
  if (stored && stored !== '新会话') return stored;
  const firstUser = conversation.turns.find((turn) => turn.role === 'user');
  if (firstUser) {
    const text = firstUser.content.trim();
    return text.length > 24 ? `${text.slice(0, 23)}…` : text || '新会话';
  }
  return '新会话';
}

/**
 * Split an assistant reply into natural QQ-style bubbles: only blank lines
 * outside fenced code blocks split; short replies stay one bubble.
 */
export function splitAssistantBubbles(text: string): string[] {
  const trimmed = text.trim();
  if (trimmed.length === 0) return [];
  const lines = trimmed.split(/\r?\n/u);
  const bubbles: string[] = [];
  let current: string[] = [];
  let inFence = false;
  const flush = (): void => {
    const value = current.join('\n').trim();
    if (value.length > 0) bubbles.push(value);
    current = [];
  };
  for (const line of lines) {
    if (line.trimStart().startsWith('```')) inFence = !inFence;
    if (!inFence && line.trim().length === 0) {
      flush();
      continue;
    }
    current.push(line);
  }
  flush();
  return bubbles;
}

export interface QueuedChat {
  id: string;
  text: string;
  conversation_id: string;
}

export const MAX_QUEUED_CHATS = 20;

/** Append a message to the local send queue, dropping the oldest beyond the cap. */
export function enqueueQueuedChat(
  queue: readonly QueuedChat[],
  item: QueuedChat,
  max = MAX_QUEUED_CHATS,
): QueuedChat[] {
  return [...queue, item].slice(-Math.max(1, max));
}
